import { createStore as createStore$1, applyMiddleware, compose } from 'redux';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { setAutoFreeze, produce } from 'immer';
import { merge } from 'lodash';
import { jsx } from 'react/jsx-runtime';
import { Provider as Provider$1, useSelector as useSelector$1 } from 'react-redux';
import { createContext, useContext } from 'react';

const TYPE_SET_STATE = 'TYPE_SET_STATE';
const TYPE_GET_STATE = 'TYPE_GET_STATE';
function getActionNames(model) {
  const prototype = Object.getPrototypeOf(model);
  const names = Object.getOwnPropertyNames(prototype);
  return names.filter(method => !['constructor', 'onAction', 'subscription'].includes(method) && typeof prototype[method] === 'function' && !method.startsWith('_'));
}
class Model {
  constructor() {
    _defineProperty(this, "initialState", void 0);
    _defineProperty(this, "state", void 0);
  }
  *getState() {
    for (var _len = arguments.length, models = new Array(_len), _key = 0; _key < _len; _key++) {
      models[_key] = arguments[_key];
    }
    const result = yield {
      type: TYPE_GET_STATE,
      payload: {
        models
      }
    };
    return result;
  }
  setState(fn) {
    return {
      type: TYPE_SET_STATE,
      payload: {
        fn
      }
    };
  }
  dispatch(_) {}
}

function isGenerator(obj) {
  return obj && typeof obj.next === 'function' && typeof obj.throw === 'function';
}

setAutoFreeze(false);
const applyOnAction = (fns, action, type) => {
  for (let fn of fns) {
    action = fn(action, type);
  }
  return action;
};
const bindActionCreators = (action, store) => {
  for (let index in action) {
    let method = action[index];
    action[index] = function () {
      let result = method(...arguments);
      if (typeof result === 'function') {
        return result(store);
      }
    };
  }
  return action;
};
function createStore(models) {
  let initialStates = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let routerWrapper = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : reducer => reducer;
  let middleware = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  const appReducers = {};
  const appActions = {};
  const appStates = {};
  const boundActions = {};
  const subscriptions = {};
  const onActions = {};
  for (const name in models) {
    const model = models[name];
    if (model.initialState !== undefined) {
      if (typeof model.initialState === 'function') {
        appStates[name] = model.initialState(initialStates[name], initialStates);
      } else {
        appStates[name] = merge(model.initialState, initialStates[name]);
      }
    } else {
      appStates[name] = initialStates[name];
    }
    appActions[name] = {};
    if (model.onAction) {
      onActions[name] = (action, type) => model.onAction(action, type, boundActions[name]);
    }
    if (model.subscription) {
      subscriptions[name] = model.subscription;
    }
    model.dispatch = function (action) {
      if (typeof action === 'string') {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }
        boundActions[name][action](...args);
      }
    };
    const actionNames = getActionNames(model);
    for (let actionName of actionNames) {
      const prototype = Object.getPrototypeOf(model);
      const action = prototype[actionName];
      const actionType = `${name}/${actionName}`;
      appActions[name][actionName] = function () {
        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }
        return async store => {
          const {
            dispatch,
            getState
          } = store;
          let index = 0;
          const beforeActions = Object.entries(onActions).filter(_ref => {
            let [model] = _ref;
            return model !== name;
          }).map(_ref2 => {
            let [, fn] = _ref2;
            return fn;
          });
          let generator = applyOnAction(beforeActions, action.bind(model), actionType)(...args);
          const runGenerator = async generator => {
            let iter = await generator.next();
            while (!iter.done) {
              let value, error;
              if (iter.value instanceof Promise) {
                try {
                  value = await iter.value;
                } catch (e) {
                  error = e;
                }
              } else if (isGenerator(iter.value)) {
                try {
                  value = await runGenerator(iter.value);
                } catch (e) {
                  error = e;
                }
              } else {
                let action = iter.value;
                let payload = action.payload;
                switch (action.type) {
                  case TYPE_GET_STATE:
                    const state = getState();
                    if (payload.models.length > 0) {
                      if (payload.models.length > 1) {
                        value = payload.models.map(model => state[model]);
                      } else {
                        value = state[payload.models[0]];
                      }
                    } else {
                      value = state;
                    }
                    break;
                  case TYPE_SET_STATE:
                    let type = `${actionType}/@@${++index}`;
                    appReducers[type] = payload.fn;
                    dispatch({
                      type,
                      name
                    });
                    delete appReducers[type];
                    break;
                }
              }
              if (error) {
                iter = await generator.throw(error);
              } else {
                iter = await generator.next(value);
              }
            }
            return iter.value;
          };
          await runGenerator(generator);
        };
      };
    }
  }
  const appReducer = function (state, action) {
    return produce(state, draft => {
      if (action.name && appReducers[action.type]) {
        if (typeof appReducers[action.type] === 'function') {
          draft[action.name] = produce(draft[action.name], appReducers[action.type]);
        } else {
          draft[action.name] = appReducers[action.type];
        }
      }
    });
  };
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  const store = createStore$1(routerWrapper(appReducer), appStates, composeEnhancers(applyMiddleware(...middleware)));
  const {
    getState
  } = store;
  for (let [name, action] of Object.entries(appActions)) {
    boundActions[name] = bindActionCreators(action, store);
  }
  //执行订阅
  for (let name in subscriptions) {
    subscriptions[name].call(models[name], boundActions[name], getState()[name]);
  }
  store.actions = boundActions;
  return store;
}

const Context = createContext({
  actions: {}
});

function Provider(_ref) {
  let {
    store,
    children
  } = _ref;
  return jsx(Context.Provider, {
    value: {
      actions: store.actions
    },
    children: jsx(Provider$1, {
      store: store,
      children: children
    })
  });
}

function crateApplication(models) {
  let initialState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const store = createStore(models, initialState);
  return _ref => {
    let {
      children
    } = _ref;
    return jsx(Provider, {
      store: store,
      children: children
    });
  };
}

function useActions (namespace) {
  const {
    actions
  } = useContext(Context);
  if (namespace) {
    return actions[namespace];
  }
  return actions;
}

function useSelector(selector, equalityFn) {
  if (typeof selector === 'string' || Array.isArray(selector)) {
    return useSelector$1(state => {
      return Array.isArray(selector) ? selector : Array(selector).reduce((state, selector) => {
        return state[selector];
      }, state);
    }, equalityFn);
  }
  return useSelector$1(selector, equalityFn);
}

export { Model, Provider, crateApplication as createApplication, createStore, useActions, useSelector };
